import React, { useContext } from 'react';
import { isUndefined } from 'lodash';
import { DROPDOWN_TYPE_LABEL, QUESTION_TYPE_CHOICES } from 'common/constants/firms';
import { FormDialog } from 'components';
import CustomPrimaryButton from 'components/CustomPrimaryButton/CustomPrimaryButton';
import { DocumentsContext } from 'context/DocumentsContext';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import useRequestDocsAndQuestions from 'services/hooks/useRequestDocsAndQuestions/useRequestDocsAndQuestions';
import { AddOrUpdateTaskDialogProps } from './types';
import UpdateDocumentContent from './UpdateDocumentContent';
import QuestionContent from '../../RequestItemModalContent/QuestionContent';
import RequestItemModalContent from '../../RequestItemModalContent/RequestItemModalContent';

const AddOrUpdateTaskDialog = ({
  isDocumentRequest,
  firmId,
  companyId,
  setUpdateTaskList,
  isInitialSetup,
  primaryInformationRequestId,
  currentPageData,
  openAddOrUpdateTaskDialog,
  setOpenAddOrUpdateTaskDialog,
  isEdit,
  setIsEdit,
  isMaxCharactersError,
  setIsMaxCharactersError,
  formInitialValues,
  addOrUpdateTaskDialogFormValues,
  setAddOrUpdateTaskDialogFormValues,
  isCompanyTemplate,
}: AddOrUpdateTaskDialogProps) => {
  const documentsContext = useContext(DocumentsContext);
  const informationDashboardContext = useContext(InformationDashboard409AContext);
  // This is because the modal can be called from the Reference Widget, Documents page or Information page (Company Dashboard)
  const { currentDocuments } = documentsContext || informationDashboardContext || {};
  const isNameInvalid = !addOrUpdateTaskDialogFormValues.name || isMaxCharactersError;
  const isQuestionTypeInvalid = isDocumentRequest ? false : isUndefined(addOrUpdateTaskDialogFormValues.question_type);
  const disableCreateButton = isDocumentRequest ? isNameInvalid : isNameInvalid || isQuestionTypeInvalid;
  const disabledButtonTooltip = isDocumentRequest
    ? 'Document name is required'
    : 'Question and answer type are required';

  const dropdownTypeValue = QUESTION_TYPE_CHOICES.find(choice => choice.label === DROPDOWN_TYPE_LABEL)?.value;
  const { handleRequestNewItem, handleUpdateTask, handleCancel }
    = useRequestDocsAndQuestions({
      formInitialValues,
      formValues: addOrUpdateTaskDialogFormValues,
      isDocumentRequest,
      firmId,
      companyId: companyId ?? null,
      setUpdateTaskList,
      setOpen: setOpenAddOrUpdateTaskDialog,
      setIsEdit,
      setFormValues: setAddOrUpdateTaskDialogFormValues,
      isInitialSetup,
      primaryInformationRequestId,
      currentPageData,
    });

  return (
    <FormDialog
      open={openAddOrUpdateTaskDialog}
      onClose={handleCancel}
      title={`${isEdit ? 'UPDATE' : 'CREATE NEW'} ${isDocumentRequest ? 'DOCUMENT REQUEST' : 'QUESTION'}`}
      disableTitleClose={false}
      showSaveButton={false}
      customCancelButton={
        <CustomPrimaryButton onClick={handleCancel} variant="outlined">
            Cancel
        </CustomPrimaryButton>
      }
      customButton={
        <CustomPrimaryButton
          onClick={isEdit ? handleUpdateTask : handleRequestNewItem}
          variant="contained"
          disabled={disableCreateButton}
          tooltip={disableCreateButton ? disabledButtonTooltip : ''}>
          {`${isEdit ? 'UPDATE' : 'CREATE'} ${isDocumentRequest ? 'DOCUMENT' : 'QUESTION'}`}
        </CustomPrimaryButton>
      }>
      <RequestItemModalContent
        isDocumentRequest={isDocumentRequest}
        formValues={addOrUpdateTaskDialogFormValues}
        setFormValues={setAddOrUpdateTaskDialogFormValues}
        hideDescription={isInitialSetup}
        setIsMaxCharactersError={setIsMaxCharactersError}>
        {isDocumentRequest
          ? <UpdateDocumentContent
            formValues={addOrUpdateTaskDialogFormValues}
            setFormValues={setAddOrUpdateTaskDialogFormValues}
            currentDocuments={currentDocuments}
            isCompanyTemplate={isCompanyTemplate}
          /> : (
            <QuestionContent
              formValues={addOrUpdateTaskDialogFormValues}
              setFormValues={setAddOrUpdateTaskDialogFormValues}
              setIsMaxCharactersError={setIsMaxCharactersError}
              dropdownTypeValue={dropdownTypeValue}
              isEdit={isEdit}
            />
          )}
      </RequestItemModalContent>
    </FormDialog>
  );
};

export default AddOrUpdateTaskDialog;
